import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { AlertTriangle, Clock, Car, Camera, Wrench } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { supabase } from "@/integrations/supabase/client"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { toast } from "sonner"

type Urgencia = "ok" | "atencao" | "urgente"

const URGENCIA_CONFIG: Record<Urgencia, { label: string; className: string }> = {
  ok:      { label: "OK",      className: "border-green-200 bg-green-50 text-green-700" },
  atencao: { label: "Atenção", className: "border-amber-200 bg-amber-50 text-amber-700" },
  urgente: { label: "Urgente", className: "border-red-200 bg-red-50 text-red-700" },
}

interface DiagItem {
  id: string
  titulo: string
  observacao: string | null
  urgencia: Urgencia
  foto_url: string | null
  ordem: number
}

interface DiagData {
  titulo: string
  observacoes: string | null
  criado_em: string
  loja_nome: string
  status: string
  cliente_nome: string | null
  cliente_telefone: string | null
  cliente_ciente_nome: string | null
  cliente_ciente_em: string | null
  cliente_autorizou_em: string | null
  veiculo: {
    placa: string; marca: string; modelo: string; ano: number
    anoModelo: string | null; cor: string | null; quilometragem: number | null
  }
  itens: DiagItem[]
}

export default function DiagnosticoPublico() {
  const { token } = useParams<{ token: string }>()
  const [dados, setDados] = useState<DiagData | null>(null)
  const [itensResolvidos, setItensResolvidos] = useState<DiagItem[]>([])
  const [clienteNome, setClienteNome] = useState("")
  const [registrando, setRegistrando] = useState<"ciente" | "autorizado" | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (token) void fetchDiagnostico(token)
  }, [token])

  const fetchDiagnostico = async (t: string) => {
    try {
      const { data, error } = await supabase.rpc("get_diagnostico_publico", { p_token: t })
      if (error || !data) { setNotFound(true); return }

      const diag = data as DiagData
      setDados(diag)
      setClienteNome(diag.cliente_ciente_nome ?? diag.cliente_nome ?? "")

      const resolvidos = await Promise.all(
        diag.itens.map(async (item) => ({
          ...item,
          foto_url: item.foto_url
            ? (await createSignedFileUrl(PHOTO_BUCKET, item.foto_url)) ?? item.foto_url
            : null,
        }))
      )
      setItensResolvidos(resolvidos)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const registrarCiencia = async (autorizar: boolean) => {
    if (!token) return
    if (!clienteNome.trim()) {
      toast.error("Informe seu nome para registrar a ciência.")
      return
    }

    setRegistrando(autorizar ? "autorizado" : "ciente")
    try {
      const { data, error } = await supabase.rpc("registrar_ciencia_diagnostico_publico", {
        p_token: token,
        p_nome: clienteNome.trim(),
        p_autorizar: autorizar,
      })
      if (error) throw error
      const resposta = data as { ok?: boolean } | null
      if (!resposta?.ok) throw new Error("not_found")
      toast.success(autorizar ? "Autorização registrada" : "Ciência registrada")
      await fetchDiagnostico(token)
    } catch {
      toast.error("Não foi possível registrar agora.")
    } finally {
      setRegistrando(null)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (notFound || !dados) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <AlertTriangle className="h-12 w-12 text-muted-foreground opacity-40" />
        <h1 className="text-lg font-semibold">Link inválido</h1>
        <p className="text-sm text-muted-foreground max-w-xs">
          Este diagnóstico não foi encontrado ou o link é inválido.
        </p>
      </div>
    )
  }

  const { veiculo, observacoes, loja_nome, criado_em, titulo } = dados
  const dataFormatada = new Date(criado_em).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })

  const urgentes = itensResolvidos.filter(i => i.urgencia === "urgente")
  const atencao  = itensResolvidos.filter(i => i.urgencia === "atencao")
  const ok       = itensResolvidos.filter(i => i.urgencia === "ok")
  const clienteJaRegistrou = Boolean(dados.cliente_ciente_em || dados.cliente_autorizou_em)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 text-center shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
          Diagnóstico Técnico
        </p>
        <h1 className="text-lg font-bold text-foreground">{loja_nome}</h1>
        <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>{dataFormatada}</span>
        </div>
      </div>

      <div className="mx-auto max-w-lg px-4 py-5 space-y-5">

        {/* Veículo */}
        <div className="rounded-2xl bg-white border shadow-sm p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Car className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-lg font-bold tracking-widest">{veiculo.placa}</span>
                {veiculo.cor && <Badge variant="secondary" className="text-xs">{veiculo.cor}</Badge>}
              </div>
              <p className="mt-0.5 text-sm font-medium">{veiculo.marca} {veiculo.modelo}</p>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>{veiculo.ano}{veiculo.anoModelo && veiculo.anoModelo !== String(veiculo.ano) ? `/${veiculo.anoModelo}` : ""}</span>
                {veiculo.quilometragem != null && (
                  <span>{veiculo.quilometragem.toLocaleString("pt-BR")} km</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Título e observações */}
        {titulo !== "Diagnóstico Técnico" && (
          <div className="rounded-2xl bg-white border shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold">{titulo}</p>
            </div>
            {observacoes && (
              <p className="text-sm text-muted-foreground whitespace-pre-line">{observacoes}</p>
            )}
          </div>
        )}
        {titulo === "Diagnóstico Técnico" && observacoes && (
          <div className="rounded-2xl bg-white border shadow-sm p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Observações gerais
            </p>
            <p className="text-sm text-foreground whitespace-pre-line">{observacoes}</p>
          </div>
        )}

        {/* Resumo de urgência */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-red-50 border border-red-200 shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-red-700">{urgentes.length}</p>
            <p className="text-[11px] text-red-600 mt-0.5">Urgente</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{atencao.length}</p>
            <p className="text-[11px] text-amber-600 mt-0.5">Atenção</p>
          </div>
          <div className="rounded-xl bg-green-50 border border-green-200 shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{ok.length}</p>
            <p className="text-[11px] text-green-600 mt-0.5">OK</p>
          </div>
        </div>

        {/* Ciência/autorização */}
        <div className="rounded-2xl bg-white border shadow-sm p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold">Ciência do cliente</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Registre que você visualizou o diagnóstico. Se estiver de acordo, autorize o serviço por aqui.
            </p>
          </div>
          {clienteJaRegistrou ? (
            <div className="rounded-xl border bg-green-50 p-3 text-sm text-green-800">
              <p className="font-medium">
                {dados.cliente_autorizou_em ? "Serviço autorizado" : "Ciência registrada"}
              </p>
              <p className="mt-0.5 text-xs">
                {dados.cliente_ciente_nome ?? clienteNome}
                {" · "}
                {new Date(dados.cliente_autorizou_em ?? dados.cliente_ciente_em!).toLocaleString("pt-BR", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cliente-nome" className="text-xs">Nome do cliente</Label>
                <Input
                  id="cliente-nome"
                  value={clienteNome}
                  onChange={(event) => setClienteNome(event.target.value)}
                  placeholder="Nome completo"
                />
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  variant="outline"
                  onClick={() => void registrarCiencia(false)}
                  disabled={registrando !== null}
                >
                  {registrando === "ciente" ? "Registrando..." : "Estou ciente"}
                </Button>
                <Button
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={() => void registrarCiencia(true)}
                  disabled={registrando !== null}
                >
                  {registrando === "autorizado" ? "Registrando..." : "Autorizar serviço"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Itens */}
        {itensResolvidos.length > 0 && (
          <section className="space-y-2">
            {itensResolvidos.map((item) => {
              const cfg = URGENCIA_CONFIG[item.urgencia]
              return (
                <div key={item.id} className="flex gap-3 rounded-xl border bg-white shadow-sm p-3 items-start">
                  {item.foto_url ? (
                    <Dialog>
                      <DialogTrigger asChild>
                        <img
                          src={item.foto_url}
                          alt={item.titulo}
                          className="h-16 w-16 shrink-0 cursor-pointer rounded-lg object-cover border"
                        />
                      </DialogTrigger>
                      <DialogContent className="max-w-lg flex flex-col items-center gap-2 p-4">
                        <p className="text-sm font-medium">{item.titulo}</p>
                        <img
                          src={item.foto_url}
                          alt={item.titulo}
                          className="rounded-lg object-contain w-full"
                          style={{ maxHeight: "75vh" }}
                        />
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <div className="h-16 w-16 shrink-0 rounded-lg border bg-muted flex items-center justify-center">
                      <Camera className="h-5 w-5 text-muted-foreground opacity-40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-snug">{item.titulo}</p>
                    {item.observacao && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{item.observacao}</p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${cfg.className}`}>
                    {cfg.label}
                  </Badge>
                </div>
              )
            })}
          </section>
        )}

        {itensResolvidos.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-2xl border bg-white p-6 text-center">
            <Wrench className="h-8 w-8 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground">Nenhum item registrado neste diagnóstico.</p>
          </div>
        )}

        {/* Rodapé */}
        <div className="rounded-2xl border bg-white shadow-sm p-4 text-center space-y-1">
          <p className="text-xs text-muted-foreground">
            Diagnóstico realizado por <strong>{loja_nome}</strong>
          </p>
          <p className="text-xs text-muted-foreground">{dataFormatada}</p>
        </div>

      </div>
    </div>
  )
}
